import { PipelineSeriesByRegionColour, PipelineSeriesOnBuild } from '@nova/types';
import type { ImageData as CanvasModImgData } from 'canvas';
import { Image } from 'canvas';

import { StaticPipelineOperations } from './StaticPipelineOps';
import { ServerTextureUtils } from './sv-texture-utils';
import { TextureManipulationDrawSeries } from './texture-manip-drawSeries';
import { TextureManipulationSeriesRegionMasking } from './texture-manip-drawSeriesByRegion';

// TODO: We can optimize this chain by having pipeline classes take Uint8Array, not necessarily ImageData
// They would still need to have accompanying width and height values
// And should still always accept ImageData for when they are used independently, not apart of the specific chain.

export class ServerTextureGeneration {
  public static CreateDirtLayeredTexture(gl: WebGLRenderingContext, img: Image, dirtTileImgData: CanvasModImgData): Image {
    const partImgData = ServerTextureUtils.GetImageDataFromImage(img);
    const tiledDirtPixelData: Uint8Array = StaticPipelineOperations.GetTiled(dirtTileImgData as ImageData, partImgData.width, partImgData.height, gl);
    const tiledDirtImageData: CanvasModImgData = ServerTextureUtils.GetImageDataFromPixels(tiledDirtPixelData, partImgData.width, partImgData.height);
    const tiledDirtMaskedPixelData: Uint8Array = StaticPipelineOperations.GetMasked(tiledDirtImageData as ImageData, partImgData as ImageData, false, gl);
    const tiledDirtMaskedImageData: CanvasModImgData =
      ServerTextureUtils.GetImageDataFromPixels(tiledDirtMaskedPixelData, partImgData.width, partImgData.height);
    const dirtyPartPixelData: Uint8Array = StaticPipelineOperations.GetOverlayed(partImgData as ImageData, tiledDirtMaskedImageData as ImageData, gl);
    const dirtyPartImageData: CanvasModImgData = ServerTextureUtils.GetImageDataFromPixels(dirtyPartPixelData, partImgData.width, partImgData.height);

    return ServerTextureUtils.GetImageFromImageData(dirtyPartImageData);
  }

  /**
   *
   * @param gl WebGL rendering context, as acquired through the node 'gl' module, with only works/runs in node environments. (Breaks webpack client-side builds)
   * @param img
   * @param seriesBuilt
   * @returns
   */
  public static CreateFromPipelineSeries(gl: WebGLRenderingContext, img: Image, seriesBuilt: PipelineSeriesOnBuild): Image {
    if (seriesBuilt.length === 0) { return img; }

    // We'll just create and use this functionality once.
    // TODO: Establish any destructors required in the manipulation objects
    const texManipSeries: TextureManipulationDrawSeries = new TextureManipulationDrawSeries();

    const imgData = ServerTextureUtils.GetImageDataFromImage(img);
    texManipSeries.InitPipelines(gl, imgData.width, imgData.height);
    texManipSeries.CreateBaseTexture(imgData as ImageData);

    // * Essentially Just calls "RunPipelineSeries" below.
    const lastType = texManipSeries.Draw(seriesBuilt);
    const finalPixelData: Uint8Array = texManipSeries.GetPixelData(lastType);
    const finalImageData: CanvasModImgData = ServerTextureUtils.GetImageDataFromPixels(finalPixelData, imgData.width, imgData.height);
    texManipSeries.Destroy();

    return ServerTextureUtils.GetImageFromImageData(finalImageData);
  }

  /**
   *
   * @param gl WebGL rendering context, as acquired through the node 'gl' module, with only works/runs in node environments. (Breaks webpack client-side builds)
   * @param baseImg
   * @param mask
   * @param seriesByRegionColour
   * @returns
   */
  public static CreateFromPipelineSeriesByRegionColour(
    gl: WebGLRenderingContext,
    baseImg: Image, mask: Image,
    seriesByRegionColour: PipelineSeriesByRegionColour): Image {
    if (Object.keys(seriesByRegionColour).length === 0) { return baseImg; }

    const texManipSeriesMasked: TextureManipulationSeriesRegionMasking = new TextureManipulationSeriesRegionMasking();

    const baseImgData = ServerTextureUtils.GetImageDataFromImage(baseImg);
    const maskImgData = ServerTextureUtils.GetImageDataFromImage(mask);

    texManipSeriesMasked.InitPipelines(gl, baseImgData.width, baseImgData.height);
    texManipSeriesMasked.CreateBlankOutputTexture(baseImgData.width, baseImgData.height);
    texManipSeriesMasked.CreateBaseTexture(baseImgData as ImageData);
    texManipSeriesMasked.CreateMaskTexture(maskImgData as ImageData);

    const finalPixelData: Uint8Array = texManipSeriesMasked.DrawAndGetPixelData(seriesByRegionColour);
    const finalImageData: CanvasModImgData = ServerTextureUtils.GetImageDataFromPixels(finalPixelData, baseImgData.width, baseImgData.height);

    texManipSeriesMasked.Destroy();
    ServerTextureUtils.DestroyCanvas();

    return ServerTextureUtils.GetImageFromImageData(finalImageData);
  }

  public static Cleanup() {
    ServerTextureUtils.DestroyCanvas();
  }
}
